import { IntegrationCard } from '@components'

import { Button, DateTimeFormat, Flex, Tag } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useEInvoicingSetup } from '@features/eInvoicing/hooks/useEInvoicingSetup'
import { getEInvoicingBaseUrl } from '@features/eInvoicing/utils'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EInvoicingReceptionSetup } from '../../../../../../../../assets/images'
import { useSegment } from '../../../../../../../../hooks'
import { useEmberTransitionTo } from '../../../../../../../../hooks/routing/useEmberTransitionTo'
import { getCurrentLocale } from '../../../../../../../../utils/getCurrentLocale'

interface EInvoicingSetupCardProps {
  onDeactivateClick: () => void
}

export const EInvoicingSetupCard = ({ onDeactivateClick }: EInvoicingSetupCardProps): ReactElement => {
  const { t } = useTranslation()

  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { transitionTo } = useEmberTransitionTo()
  const { eInvoiceIntegrationPdpActivationDate, kycUnderReview, isEInvoicingActivated } = useEInvoicingSetup()

  const locale = getCurrentLocale()

  const handleActivateEInvoicingClick = useCallback(() => {
    const url = `${getEInvoicingBaseUrl(organization)}`
    transitionTo(url)
    track('xxx - e-invoicing: Clicked "Activate" (Client)')
  }, [organization, track, transitionTo])

  const handleDeactivateEInvoicingButtonClick = useCallback(() => {
    onDeactivateClick()
  }, [onDeactivateClick])

  return (
    <IntegrationCard>
      <IntegrationCard.Header
        title={t('receipts.side_panel.e_invoice_reception.title')}
        subtitle={isEInvoicingActivated ? 'Shine' : t('receipts.side_panel.e_invoice_reception_activate.subtitle')}
        image={<EInvoicingReceptionSetup />}
        sideActions={
          <Flex alignItems="center" justifyContent="flex-end">
            {isEInvoicingActivated ? (
              <Button variant="outlined" onClick={handleDeactivateEInvoicingButtonClick}>
                {t('receipts.side_panel.e_invoice_reception.deactivate')}
              </Button>
            ) : (
              <Button onClick={handleActivateEInvoicingClick} disabled={kycUnderReview}>
                {t('receipts.side_panel.e_invoice_reception.activate')}
              </Button>
            )}
          </Flex>
        }
      />

      {eInvoiceIntegrationPdpActivationDate ? (
        <IntegrationCard.Footer>
          <Tag
            color={isEInvoicingActivated ? 'green' : 'neutral'}
            type="subtle"
            icon={isEInvoicingActivated ? 'check' : undefined}
            size="sm"
          >
            <Trans
              i18nKey={
                isEInvoicingActivated
                  ? 'receipts.side_panel.e_invoice_reception.activated_state'
                  : 'receipts.side_panel.e_invoice_reception.deactivated_state'
              }
              values={{
                date: DateTimeFormat.getFormattedDate(eInvoiceIntegrationPdpActivationDate, locale),
              }}
            />
          </Tag>
        </IntegrationCard.Footer>
      ) : null}
    </IntegrationCard>
  )
}
